/**
 * Markdown 到领域类型的唯一转换。文本进来，Concept 或者 Issue 出去，没有第三种结果。
 * 校验全部集中在这里，之后任何模块都不再检查题目的载荷、下标范围或段落归属。
 * 用 model 与同目录的 library，RawFile 由 ports 注入，workspace 与 tools 是它的调用者。
 * 不读磁盘，不判分，也不做调度。
 */

import { load, YAMLException } from 'js-yaml'
import { z } from 'zod'
import { err, ok, type Issue, type IssueCode, type Result } from '../model/result'
import type { Concept, Section } from '../model/concept'
import {
	countBlankMarkers,
	SCHEMAS,
	type AnyQuestion,
	type DataMap,
	type ParseContext,
	type Question,
	type QuestionKind,
	type QuestionWire,
	type ReadingData,
	type LeafKind,
} from '../model/question'
import { epochDayOf, isReservedLocalId, localOfQuestion, parseConceptId, parseLocalQuestionId, questionId } from '../model/ids'
import type { ConceptId, EpochDay, RepoPath } from '../model/ids'
import type { Library } from './library'
import { buildLibrary } from './library'
import type { RawFile } from '../ports'

/** frontmatter 的线上规则。字段名与 Concept 一一对应，不一致时 parseConcept 的组装处编译失败。 */
export const FrontmatterSchema = z.object({
	id: z.string().regex(/^[a-z0-9][a-z0-9-]*$/),
	title: z.string().min(1),
	tags: z.array(z.string().min(1)).min(1),
	difficulty: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]),
	created: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
	aliases: z.array(z.string()).default([]),
})

export interface Loaded {
	readonly library: Library
	readonly issues: readonly Issue[]
	/** 无效文件跳过，有效文件照常可用。这个计数给诊断屏顶部显示。 */
	readonly skippedFiles: number
}

function issue(file: string, line: number, code: IssueCode, message: string): Issue {
	return { file, line, code, message }
}

/** Result 的 errors 已是 readonly E[]，E 就是 Issue 元素本身， variadic fail 少写一层数组。 */
function fail(...issues: readonly Issue[]): Result<never, Issue> {
	return err(...issues)
}

function isMapping(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** 一条 zod 错误翻译成带行号的 Issue。路径首段就是字段名，zod 的 message 已经是给人看的那句。 */
function fieldMessage(issues: readonly z.ZodIssue[], file: string, line: number, code: IssueCode): Issue[] {
	return issues.map((entry) => {
		const field = entry.path.join('.')
		return issue(file, line, code, field === '' ? entry.message : `${field}：${entry.message}`)
	})
}

/**
 * YAML 只有一个解析点。startLine 是这段 YAML 的第一行在文件里的行号，mark.line 是 0-based，
 * 两者相加就是人要跳过去的那一行。出错码由调用方给，frontmatter 与题块共用这段代码。
 */
function loadDocument(text: string, file: string, startLine: number, code: IssueCode): Result<unknown, Issue> {
	try {
		return ok(load(text))
	} catch (error) {
		const yaml = error instanceof YAMLException ? error : null
		const line = startLine + (yaml?.mark?.line ?? 0)
		return err(issue(file, line, code, `YAML 语法错：${yaml === null ? String(error) : yaml.reason}`))
	}
}

/** 题块围栏里那份 YAML 到 QuestionWire。字段缺失与类型都不在这里判，交给 parseQuestion 一处。 */
function wireOf(value: unknown, line: number): QuestionWire {
	const source = isMapping(value) ? value : {}
	return { id: source.id, type: source.type, prompt: source.prompt, data: source.data, explain: source.explain, line }
}

/** 一个 ```quiz 围栏：内容行、围栏首行的 1-based 行号、它挂在哪一段下。 */
interface QuizBlock {
	readonly lines: readonly string[]
	readonly fenceLine: number
	readonly sectionIndex: number
}

interface Body {
	readonly sections: readonly [Section, ...Section[]]
	readonly blocks: QuizBlock[]
	/** quiz 围栏到文件结尾都没闭合，内容永远读不全，交给 parseConcept 报错。 */
	readonly unclosedFenceLine: number | null
}

const fencePattern = /^( {0,3})(`{3,}|~{3,})(.*)$/
const headingPattern = /^(#{2,3})\s+(.*?)\s*$/

interface Fence {
	readonly marker: string
	readonly length: number
	readonly info: string
}

function fenceOf(line: string): Fence | null {
	const match = fencePattern.exec(line)
	if (match === null) return null
	const marker = match[2] as string
	return { marker, length: marker.length, info: match[3] as string }
}

/** 闭合围栏：同种记号、不短于开启它的长度、后面不带 info string。缩进已由 fencePattern 限在三格内。 */
function closes(fence: Fence, open: Fence): boolean {
	return fence.marker === open.marker && fence.length >= open.length && fence.info.trim() === ''
}

interface Draft {
	heading: string | null
	level: 2 | 3
	text: string[]
	startLine: number
	blockCount: number
}

function isBlank(line: string): boolean {
	return line.trim() === ''
}

/** Windows 换行先归一，行号与行数组下标之间才只有一套对应关系。 */
function normalizeNewlines(text: string): string {
	return text.replace(/\r\n/g, '\n')
}

/**
 * 正文切段。围栏整段从段落 markdown 里剔掉，题块单独交出去，所以正文和题目不会有两份副本。
 * 没有标题的正文自成一段（heading 为 null），于是 Concept.sections 至少有一项这个不变量在这里落地。
 * lines 从正文第一行开始，startLine 是它在文件里的 1-based 行号。
 */
function splitSections(lines: readonly string[], startLine: number): Body {
	const sections: Section[] = []
	const blocks: QuizBlock[] = []
	const draft: Draft = { heading: null, level: 2, text: [], startLine, blockCount: 0 }
	let open: Fence | null = null
	let quiz: { lines: string[]; fenceLine: number } | null = null

	const commit = (): void => {
		const empty = draft.heading === null && draft.text.every(isBlank) && draft.blockCount === 0
		if (!empty) {
			sections.push({
				heading: draft.heading,
				level: draft.level,
				markdown: draft.text.join('\n').trimEnd(),
				sourceLine: draft.startLine,
			})
		}
		draft.text = []
		draft.blockCount = 0
	}

	for (let offset = 0; offset < lines.length; offset += 1) {
		const line = lines[offset] as string
		const number = startLine + offset
		const fence = fenceOf(line)

		if (open !== null) {
			if (fence !== null && closes(fence, open)) {
				open = null
				const pending = quiz
				if (pending !== null) {
					blocks.push({ lines: pending.lines, fenceLine: pending.fenceLine, sectionIndex: sections.length })
					draft.blockCount += 1
					quiz = null
				}
			} else if (quiz !== null) {
				quiz.lines.push(line)
			} else {
				draft.text.push(line)
			}
			continue
		}

		if (fence !== null) {
			open = fence
			if (isQuizFence(fence.info)) quiz = { lines: [], fenceLine: number }
			continue
		}

		const heading = headingPattern.exec(line)
		if (heading !== null) {
			commit()
			draft.heading = heading[2] as string
			draft.level = (heading[1] as string).length === 3 ? 3 : 2
			draft.startLine = number
			continue
		}
		draft.text.push(line)
	}

	commit()
	if (sections.length === 0) sections.push({ heading: null, level: 2, markdown: '', sourceLine: startLine })
	// sections 至少一项由上面那行兜底，数组到元组这一维只在构造处交给编译器一次。
	const trailing: { fenceLine: number } | null = quiz
	return { sections: sections as [Section, ...Section[]], blocks, unclosedFenceLine: trailing === null ? null : trailing.fenceLine }
}

interface Frontmatter {
	readonly yaml: string
	readonly bodyStart: number
}

/** 缺开头或缺闭合的 --- 都算没有 frontmatter，调用方给 missing-key。 */
function splitFrontmatter(lines: readonly string[]): Frontmatter | null {
	if (lines[0] !== '---') return null
	const end = lines.findIndex((line, index) => index > 0 && line === '---')
	if (end === -1) return null
	return { yaml: lines.slice(1, end).join('\n'), bodyStart: end + 1 }
}

/** 空标题、缺字段与取值不合法都落在 frontmatter 两条码里：键不写或写成空值是 missing-key，其余 bad-value。 */
function frontmatterIssues(issues: readonly z.ZodIssue[], source: unknown, file: string): Issue[] {
	return issues.map((entry) => {
		const key = entry.path[0]
		const value = isMapping(source) && typeof key === 'string' ? source[key] : undefined
		const code: IssueCode = value === undefined || value === null ? 'frontmatter.missing-key' : 'frontmatter.bad-value'
		return fieldMessage([entry], file, 1, code)[0] as Issue
	})
}

/** YYYY-MM-DD 由 schema 挡在外面，这里只做换算。本地日历日的算法只在 ids.epochDayOf 一处。 */
function toEpochDay(created: string | undefined): EpochDay | null {
	if (created === undefined) return null
	const [year = 1970, month = 1, day = 1] = created.split('-').map(Number)
	return epochDayOf(new Date(year, month - 1, day))
}

/** 一个条目的本地题块 id 集合，含材料题的子题：条目内唯一性要一次看全。 */
function localIdsOf(question: AnyQuestion): readonly string[] {
	const children = question.kind === 'reading' ? question.data.questions : []
	return [localOfQuestion(question.id), ...children.map((child) => localOfQuestion(child.id))]
}

/**
 * 仓库级加载。跨文件唯一性（ConceptId 重复）只能在这里检查，单文件解析看不到全仓。
 * 幂等：同一批文件重复调用得到同样结果，不缓存也不增量。
 */
export function loadConcepts(files: readonly RawFile[]): Loaded {
	const issues: Issue[] = []
	const parsed: Concept[] = []
	let skippedFiles = 0

	for (const file of files) {
		if (!isConceptPath(file.path)) continue
		const result = parseConcept(file)
		if (result.ok) parsed.push(result.value)
		else {
			issues.push(...result.errors)
			skippedFiles += 1
		}
	}

	const byId = new Map<ConceptId, Concept[]>()
	for (const concept of parsed) {
		const bucket = byId.get(concept.id)
		if (bucket === undefined) byId.set(concept.id, [concept])
		else bucket.push(concept)
	}

	const kept: Concept[] = []
	for (const [id, group] of byId) {
		if (group.length > 1) {
			skippedFiles += group.length
			for (const concept of group) {
				issues.push(issue(concept.path, 1, 'concept.duplicate-id', `id ${id} 被 ${group.length} 个文件用了，全部跳过`))
			}
			continue
		}
		const concept = group[0] as Concept
		kept.push(concept)
	}

	return { library: buildLibrary(kept), issues, skippedFiles }
}

/** 单文件解析。失败时 errors 非空且不给部分结果，避免半坏的条目进队列。 */
export function parseConcept(file: RawFile): Result<Concept, Issue> {
	const lines = normalizeNewlines(file.text).split('\n')
	const frontmatter = splitFrontmatter(lines)
	if (frontmatter === null) {
		return fail(issue(file.path, 1, 'frontmatter.missing-key', '文件开头没有 --- 包起来的 frontmatter'))
	}

	const document = loadDocument(frontmatter.yaml, file.path, 2, 'frontmatter.bad-value')
	if (!document.ok) return fail(...document.errors)

	const meta = FrontmatterSchema.safeParse(document.value)
	if (!meta.success) return fail(...frontmatterIssues(meta.error.issues, document.value, file.path))

	// 字符集的真源在 ids.ts，schema 里那条 regex 与它同义。不一致时这里报错，而不是造出一个坏 id。
	const id = parseConceptId(meta.data.id)
	if (id === null) return fail(issue(file.path, 1, 'frontmatter.bad-value', 'id 不符合 [a-z0-9][a-z0-9-]*'))

	const body = lines.slice(frontmatter.bodyStart)
	const { sections, blocks, unclosedFenceLine } = splitSections(body, frontmatter.bodyStart + 1)

	const questions: AnyQuestion[] = []
	const issues: Issue[] = []
	const seenLocals = new Map<string, number>()
	if (unclosedFenceLine !== null) {
		issues.push(issue(file.path, unclosedFenceLine, 'question.block-yaml', 'quiz 围栏没有闭合，后面的内容不会被当成正文'))
	}

	for (const block of blocks) {
		const blockDocument = loadDocument(block.lines.join('\n'), file.path, block.fenceLine + 1, 'question.block-yaml')
		if (!blockDocument.ok) {
			issues.push(...blockDocument.errors)
			continue
		}
		const ctx: ParseContext = { conceptId: id, file: file.path, sectionIndex: block.sectionIndex, parentQuestionId: null }
		const parsed = parseQuestion(wireOf(blockDocument.value, block.fenceLine), ctx)
		if (!parsed.ok) {
			issues.push(...parsed.errors)
			continue
		}
		questions.push(parsed.value)
		for (const local of localIdsOf(parsed.value)) {
			const firstLine = seenLocals.get(local)
			if (firstLine !== undefined) {
				issues.push(issue(file.path, parsed.value.sourceLine, 'question.duplicate-id', `题块 id ${local} 在本条目内重复（先在 ${firstLine} 行出现过）`))
			} else {
				seenLocals.set(local, parsed.value.sourceLine)
			}
		}
	}

	if (issues.length > 0) return fail(...issues)
	return ok({
		id,
		path: file.path,
		title: meta.data.title,
		tags: meta.data.tags,
		difficulty: meta.data.difficulty,
		createdOn: toEpochDay(meta.data.created),
		aliases: meta.data.aliases,
		sections,
		questions,
	})
}

interface Shell {
	readonly local: string
	readonly kind: QuestionKind
	readonly prompt: string
	readonly explain: string | null
}

/**
 * 题块外壳。顺序是刻意的：保留字先于字符集，未登记题型先于载荷，
 * 这样 `recall:` 开头的 id 报 reserved-id，写错的 type 不会被一串载荷错误盖住。
 */
function readShell(wire: QuestionWire, ctx: ParseContext, issues: Issue[]): Shell | null {
	let local: string | null = null
	if (typeof wire.id !== 'string') {
		issues.push(issue(ctx.file, wire.line, 'question.bad-data', '题块缺少 id 或 id 不是字符串'))
	} else if (isReservedLocalId(wire.id)) {
		issues.push(issue(ctx.file, wire.line, 'question.reserved-id', `id ${wire.id} 用了保留前缀，recall 归派生的知识点卡`))
	} else {
		local = parseLocalQuestionId(wire.id)
		if (local === null) issues.push(issue(ctx.file, wire.line, 'question.bad-data', `id ${wire.id} 不符合 [a-z0-9][a-z0-9-]*`))
	}

	const type = wire.type
	let kind: QuestionKind | null = null
	if (typeof type === 'string' && Object.hasOwn(SCHEMAS, type)) kind = type as QuestionKind
	else issues.push(issue(ctx.file, wire.line, 'question.unknown-type', `未登记的题型 ${JSON.stringify(type)}`))

	let prompt: string | null = null
	if (typeof wire.prompt !== 'string' || wire.prompt.trim() === '') {
		issues.push(issue(ctx.file, wire.line, 'question.bad-data', 'prompt 必填'))
	} else {
		prompt = wire.prompt
	}

	let explain: string | null = null
	if (wire.explain !== undefined) {
		if (typeof wire.explain === 'string') explain = wire.explain
		else issues.push(issue(ctx.file, wire.line, 'question.bad-data', 'explain 不是字符串'))
	}

	if (local === null || kind === null || prompt === null) return null
	return { local, kind, prompt, explain }
}

/** blank 的空位数是唯一一处两个字段互相核对的地方，单独报 blank-count，schema 的 refine 只会给笼统的 bad-data。 */
function blankCountIssue(data: unknown, ctx: ParseContext, line: number): Issue | null {
	if (!isMapping(data) || typeof data.text !== 'string' || !Array.isArray(data.blanks)) return null
	const markers = countBlankMarkers(data.text)
	const specs = data.blanks.length
	if (markers === specs) return null
	return issue(ctx.file, line, 'question.blank-count', `text 里 ${markers} 个空位，blanks 写了 ${specs} 项`)
}

/**
 * 一道题（含材料题的子题）。kind 未登记时返回 Issue，不猜。
 * 保留字 id → question.reserved-id；未登记 type → question.unknown-type；schema 不过 → question.bad-data
 * reading 的子题递归调用自身，子题 type 为 reading 时返回 question.nested-reading
 */
export function parseQuestion(wire: QuestionWire, ctx: ParseContext): Result<AnyQuestion, Issue> {
	const issues: Issue[] = []
	const shell = readShell(wire, ctx, issues)
	// readShell 返回 null 的充要条件就是它记了至少一条 Issue，所以失败一定有非空错误列表。
	if (shell === null) return fail(...issues)

	const id = questionId(ctx.conceptId, shell.local)
	const build = (data: DataMap[QuestionKind]): Result<AnyQuestion, Issue> => {
		// kind 是运行时字符串，编译器认不出它和 data 同源，所以域内形状只在这一个构造点交给类型。
		const question: Question<QuestionKind> = {
			id,
			conceptId: ctx.conceptId,
			kind: shell.kind,
			prompt: shell.prompt,
			explain: shell.explain,
			data,
			sectionIndex: ctx.sectionIndex,
			sourceLine: wire.line,
		}
		return ok(question as AnyQuestion)
	}

	if (shell.kind === 'reading') {
		const payload = SCHEMAS.reading.safeParse(wire.data)
		if (!payload.success) return fail(...fieldMessage(payload.error.issues, ctx.file, wire.line, 'question.bad-data'))
		const children: Question<LeafKind>[] = []
		for (const raw of payload.data.questions) {
			const child = wireOf(raw, wire.line)
			if (child.type === 'reading') {
				issues.push(issue(ctx.file, wire.line, 'question.nested-reading', `材料题 ${shell.local} 的子题 ${JSON.stringify(child.id)} 又是材料题`))
				continue
			}
			const result = parseQuestion(child, { ...ctx, parentQuestionId: id })
			if (!result.ok) {
				issues.push(...result.errors)
				continue
			}
			children.push(result.value as Question<LeafKind>)
		}
		if (issues.length > 0) return fail(...issues)
		const data: ReadingData = {
			passage: payload.data.passage,
			// schema 的 min(1) 保证至少一个子题，数组到元组这一维只能在构造处交给编译器。
			questions: children as [Question<LeafKind>, ...Question<LeafKind>[]],
		}
		return build(data)
	}

	if (shell.kind === 'blank') {
		const count = blankCountIssue(wire.data, ctx, wire.line)
		if (count !== null) return fail(count)
	}

	const schema: z.ZodType = SCHEMAS[shell.kind]
	const payload = schema.safeParse(wire.data)
	if (!payload.success) return fail(...fieldMessage(payload.error.issues, ctx.file, wire.line, 'question.bad-data'))

	// schema 的输出与域内载荷同名同形，缺省的 normalize 已由 schema 补成 []。
	// 数组到元组那一维（options 至少两项、blanks 至少一项）类型表达不了，连同 kind 与 data 的相关性一起在这处交给编译器。
	const data = payload.data as DataMap[QuestionKind]
	return build(data)
}

/** 只有 concepts 目录下的 md 文件进索引，README 与 assets 不算。 */
export function isConceptPath(path: RepoPath): boolean {
	return path.startsWith('concepts/') && path.endsWith('.md')
}

/** 围栏 info string 必须正好是 quiz。带参数的写法视为普通代码块，不解析。 */
export function isQuizFence(info: string): boolean {
	return info.trim() === 'quiz'
}
